import React, { useState, useEffect } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  BuildingOffice2Icon,
  UserIcon,
  PhoneIcon,
  DocumentTextIcon,
  MapPinIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../config/api';

interface PendingRegistration {
  id: string;
  organization_name: string;
  business_registration_number: string;
  business_name: string;
  address: string;
  phone: string;
  owner_name: string;
  owner_email: string;
  owner_phone: string;
  submitted_at: string;
}

interface Statistics {
  pending: number;
  approved: number;
  rejected: number;
  total: number;
}

const OwnerApprovalPage: React.FC = () => {
  const [registrations, setRegistrations] = useState<PendingRegistration[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRegistration, setSelectedRegistration] = useState<PendingRegistration | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [approveTarget, setApproveTarget] = useState<PendingRegistration | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchPendingRegistrations(),
        fetchStatistics()
      ]);
    } catch (error) {
      console.error('데이터 로드 오류:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPendingRegistrations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/owner-approvals/pending`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRegistrations(data.data.registrations);
      }
    } catch (error) {
      console.error('가입 신청 목록 조회 오류:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/owner-approvals/statistics`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatistics(data.data);
      }
    } catch (error) {
      console.error('통계 조회 오류:', error);
    }
  };

  const handleApprove = async (registration: PendingRegistration) => {
    setApproveTarget(null);
    setIsProcessing(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/owner-approvals/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          organization_id: registration.id
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || '승인 처리에 실패했습니다');
      }

      alert('가입 신청이 승인되었습니다.');
      setShowDetailModal(false);
      await fetchData(); // 목록 새로고침

    } catch (error) {
      console.error('승인 처리 오류:', error);
      alert(error instanceof Error ? error.message : '승인 처리 중 오류가 발생했습니다');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRegistration) return;

    if (!rejectionReason.trim()) {
      alert('거부 사유를 입력해주세요');
      return;
    }

    setIsProcessing(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/owner-approvals/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          organization_id: selectedRegistration.id,
          reason: rejectionReason
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || '거부 처리에 실패했습니다');
      }

      alert('가입 신청이 거부되었습니다.');
      setShowRejectModal(false);
      setShowDetailModal(false);
      setRejectionReason('');
      await fetchData(); // 목록 새로고침

    } catch (error) {
      console.error('거부 처리 오류:', error);
      alert(error instanceof Error ? error.message : '거부 처리 중 오류가 발생했습니다');
    } finally {
      setIsProcessing(false);
    }
  };

  const openDetailModal = (registration: PendingRegistration) => {
    setSelectedRegistration(registration);
    setShowDetailModal(true);
  };

  const openRejectModal = () => {
    setShowRejectModal(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatBusinessNumber = (number: string) => {
    // XXX-XX-XXXXX 형식으로 변환
    const cleaned = number.replace(/[^0-9]/g, '');
    if (cleaned.length === 10) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5)}`;
    }
    return number;
  };

  return (
    <div className="p-6">
      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">오너 가입 승인 관리</h1>
        <p className="text-gray-600 mt-1">새로운 독서실/학원 오너의 가입 신청을 검토하고 승인합니다</p>
      </div>

      {/* 통계 카드 */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 shadow-sm border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">승인 대기</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.pending}</p>
              </div>
              <ClockIcon className="w-8 h-8 text-yellow-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">승인 완료</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.approved}</p>
              </div>
              <CheckCircleIcon className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">거부됨</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.rejected}</p>
              </div>
              <XCircleIcon className="w-8 h-8 text-red-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">전체</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.total}</p>
              </div>
              <BuildingOffice2Icon className="w-8 h-8 text-blue-500" />
            </div>
          </div>
        </div>
      )}

      {/* 가입 신청 목록 */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">승인 대기 목록</h2>
        </div>

        {isLoading ? (
          <div className="p-12 text-center text-gray-500">
            로딩 중...
          </div>
        ) : registrations.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            승인 대기 중인 가입 신청이 없습니다
          </div>
        ) : (
          <div className="divide-y">
            {registrations.map((registration) => (
              <div key={registration.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <BuildingOffice2Icon className="w-5 h-5 text-gray-400" />
                      <h3 className="text-lg font-semibold text-gray-800">
                        {registration.organization_name}
                      </h3>
                      <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                        승인 대기
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <UserIcon className="w-4 h-4" />
                        <span>{registration.owner_name} ({registration.owner_email})</span>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <DocumentTextIcon className="w-4 h-4" />
                        <span>사업자번호: {formatBusinessNumber(registration.business_registration_number)}</span>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <PhoneIcon className="w-4 h-4" />
                        <span>{registration.phone}</span>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <ClockIcon className="w-4 h-4" />
                        <span>신청일: {formatDate(registration.submitted_at)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => openDetailModal(registration)}
                      className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      상세보기
                    </button>
                    <button
                      onClick={() => setApproveTarget(registration)}
                      disabled={isProcessing}
                      className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      <CheckCircleIcon className="w-4 h-4 inline mr-1" />
                      승인
                    </button>
                    <button
                      onClick={() => {
                        setSelectedRegistration(registration);
                        openRejectModal();
                      }}
                      disabled={isProcessing}
                      className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      <XCircleIcon className="w-4 h-4 inline mr-1" />
                      거부
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 상세보기 모달 */}
      {showDetailModal && selectedRegistration && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowDetailModal(false)}
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-800">가입 신청 상세 정보</h3>
            </div>

            <div className="p-6 space-y-6">
              {/* 사업장 정보 */}
              <div>
                <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <BuildingOffice2Icon className="w-5 h-5" />
                  사업장 정보
                </h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">독서실/학원명</p>
                      <p className="font-medium">{selectedRegistration.organization_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">사업자명 (상호)</p>
                      <p className="font-medium">{selectedRegistration.business_name}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">사업자등록번호</p>
                    <p className="font-medium">{formatBusinessNumber(selectedRegistration.business_registration_number)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">사업장 주소</p>
                    <p className="font-medium">{selectedRegistration.address}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">사업장 전화번호</p>
                    <p className="font-medium">{selectedRegistration.phone}</p>
                  </div>
                </div>
              </div>

              {/* 담당자 정보 */}
              <div>
                <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <UserIcon className="w-5 h-5" />
                  담당자 정보
                </h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">이름</p>
                      <p className="font-medium">{selectedRegistration.owner_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">연락처</p>
                      <p className="font-medium">{selectedRegistration.owner_phone}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">이메일</p>
                    <p className="font-medium">{selectedRegistration.owner_email}</p>
                  </div>
                </div>
              </div>

              {/* 신청 정보 */}
              <div>
                <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <ClockIcon className="w-5 h-5" />
                  신청 정보
                </h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">신청 일시</p>
                  <p className="font-medium">{formatDate(selectedRegistration.submitted_at)}</p>
                </div>
              </div>

              {/* 안내 */}
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>승인 시 자동 처리</strong><br />
                  • 오너 계정 활성화<br />
                  • 승인 알림 이메일 발송
                </p>
              </div>
            </div>

            <div className="p-6 border-t flex gap-3">
              <button
                onClick={() => setShowDetailModal(false)}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                닫기
              </button>
              <button
                onClick={openRejectModal}
                disabled={isProcessing}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                거부
              </button>
              <button
                onClick={() => setApproveTarget(selectedRegistration)}
                disabled={isProcessing}
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {isProcessing ? '처리 중...' : '승인'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 승인 확인 모달 */}
      {approveTarget && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setApproveTarget(null)}
        >
          <div
            className="bg-white rounded-lg max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-800">가입 신청 승인</h3>
            </div>

            <div className="p-6">
              <p className="text-gray-600">
                {approveTarget.organization_name}의 가입 신청을 승인하시겠습니까?
              </p>
              <div className="mt-4 bg-blue-50 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  승인하면 오너 계정이 활성화되고 승인 알림이 발송됩니다.
                </p>
              </div>
            </div>

            <div className="p-6 border-t flex gap-3">
              <button
                onClick={() => setApproveTarget(null)}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                onClick={() => handleApprove(approveTarget)}
                disabled={isProcessing}
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {isProcessing ? '처리 중...' : '승인 확정'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 거부 모달 */}
      {showRejectModal && selectedRegistration && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowRejectModal(false);
            setRejectionReason('');
          }}
        >
          <div
            className="bg-white rounded-lg max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b">
              <h3 className="text-xl font-semibold text-gray-800">가입 신청 거부</h3>
            </div>

            <div className="p-6">
              <p className="text-gray-600 mb-4">
                {selectedRegistration.organization_name}의 가입 신청을 거부하시겠습니까?
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  거부 사유 *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  rows={4}
                  placeholder="거부 사유를 입력해주세요 (신청자에게 전달됩니다)"
                />
              </div>

              <div className="mt-4 bg-amber-50 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>주의:</strong> 거부된 신청은 복구할 수 없습니다.
                </p>
              </div>
            </div>

            <div className="p-6 border-t flex gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleReject}
                disabled={isProcessing || !rejectionReason.trim()}
                className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isProcessing ? '처리 중...' : '거부 확정'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerApprovalPage;
